import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { asyncScheduler, fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { MAX_BASKETITEM_COUNT, PaymentType, ProcessCard, SOTBasketProductViewModel, SOTBasketVatViewModel, SOTProductViewModel } from '../../models';
import { SOTService, VoucherActionType } from '../../services';

@Component({
  selector: "sot-checkout",
  templateUrl: "./checkout.component.html",
  styleUrls: ["./checkout.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutComponent implements OnInit, AfterViewInit, OnDestroy {

  /** artikel container zum scrollen */
  @ViewChild("product_list") product_list_div: ElementRef;

  private _basketSubscription: Subscription;

  /** inital scroll position */
  private _init_scroll_position: number;

  /** zeigt hinweis das man nach unten scrollen kann */
  public show_product_list_bottom_scroll: boolean;
  private _productScrollSubscription: Subscription;
  private _productResizeObserver: any; // ResizeObserver
  public isModeOne: boolean = false;
  public isModeTwo: boolean = false;
  public processCard: null | ProcessCard;
  public get cardUsedAmount(): number {
    return this._sotService.cardTotalAmount;
  }
  public get isCardUnderLimit(): boolean {
    return this._sotService.isCardUnderLimit;
  }

  public constructor(private _changeDetectorRef: ChangeDetectorRef
    , private _sotService: SOTService) {
    console.debug("--- sot-checkout constructor")
    this.show_product_list_bottom_scroll = false;
  }

  public ngOnInit(): void {
    console.debug("--- sot-checkout ngOnInit")
    const me = this;
    const params = me._sotService.GetAndClearCheckoutComponentBuffer();
    if (params) {
      me._init_scroll_position = params.scroll_position;
    }
    me._basketSubscription = me._sotService.GetBasket().BasketChanged.subscribe(() => me.PushChanges());
    me.processCard = me._sotService.currentCard;
    me.startRecomendationAutoScroll();
    me.isModeOne = me._sotService.useModeOne();
    me.isModeTwo = me._sotService.useModeTwo();
    me.PushChanges();
  }

  public ngAfterViewInit(): void {
    console.debug("--- sot-checkout ngAfterViewInit")
    const me = this;
    // after view init, da dann die höhe bekannt ist
    const product_list_el = me.product_list_div.nativeElement;
    if (product_list_el.scrollHeight > product_list_el.offsetHeight) {
      me.show_product_list_bottom_scroll = true; // show bottom scroll initial
      me.PushChanges();
    }
    const checkProductListScroll = () => {
      // toggles show_product_list_bottom_scroll when scrolled to the bottom or not
      const at_bottom = (product_list_el.scrollTop + product_list_el.offsetHeight) >= (product_list_el.scrollHeight - 5 /*5=bottom_offset*/);
      if (at_bottom === me.show_product_list_bottom_scroll) {
        me.show_product_list_bottom_scroll = !at_bottom;
        me.PushChanges();
      }
    };
    // immer binden, da der content dynamisch ist
    // Subscribe to a throttled scrollEvent from ProductList
    me._productScrollSubscription = fromEvent(me.product_list_div.nativeElement, "scroll").pipe(
      throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
    ).subscribe(() => { checkProductListScroll(); });

    // start observing for resize product_list_el // todo besser auf ein click reagieren, da es nicht jeden cahnge mitbekommt, nach max-height wird es nicht getriggert
    me._productResizeObserver = new (window as any).ResizeObserver(() => { checkProductListScroll(); });
    me._productResizeObserver.observe(product_list_el);

    if (me._init_scroll_position > 0) {
      me.scroll_position = me._init_scroll_position;
      me.PushChanges();
    }
  }

  public ngOnDestroy(): void {
    console.debug("--- sot-checkout ngOnDestroy")
    const me = this;
    if (me._productScrollSubscription) {
      me._productScrollSubscription.unsubscribe();
    }
    if (me._productResizeObserver) {
      me._productResizeObserver.unobserve(me.product_list_div.nativeElement);
    }
    if (me._basketSubscription) {
      me._basketSubscription.unsubscribe();
    }
    me.stopRecomendationAutoScroll();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._sotService.coreLogService.error("CheckoutComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public showCardlimit(): void {
    this._sotService.OpenCardlimitDialog();
  }

  public back(): void {
    this._sotService.GoToMenu(null);
  }

  public get products(): SOTBasketProductViewModel[] {
    return this._sotService.GetBasket().products;
  }

  public removeProduct(product: SOTBasketProductViewModel): void {
    const me = this;
    if (me.isBenefitItem(product)) {
      me._sotService.openBonusActionDialogToEditBenefit(true);
    } else {
      me._sotService.GetBasket().removeBasketProduct(product);
      me.PushChanges();
    }
  }

  public productAmountChanged(product: SOTBasketProductViewModel): void {
    const me = this;
    if (product.count == 0) {
      me.removeProduct(product);
    }
  }

  /** ermittelt die actuelle scroll position in der bestellübersicht */
  private get scroll_position(): number {
    try {
      return this.product_list_div.nativeElement.scrollTop;
    } catch (error) {
      this._sotService.coreLogService.error("CheckoutComponent.scroll_position", "try catch", error);
      return 0;
    }
  }

  /** setzt die scroll position in der bestellübersicht */
  private set scroll_position(position: number) {
    try {
      this.product_list_div.nativeElement.scrollTop = position;
    } catch (error) {
      this._sotService.coreLogService.error("CheckoutComponent.scroll_position set", "try catch", error);
    }
  }

  public openProduct(recomendation: SOTProductViewModel, itemGroup): void {
    const me = this;
    const scroll_position = me.scroll_position; // calc and save it
    me._sotService.OpenProduct(recomendation, itemGroup, null, () => {
      me._sotService.GoToCheckout(scroll_position);
    });
  }

  public editProduct(product: SOTBasketProductViewModel): void {
    const me = this;
    const scroll_position = me.scroll_position; // calc and save it
    const productEdit = me._sotService.GetBasket().getProductForEdit(product);
    me._sotService.OpenEditBasketProduct(productEdit, () => {
      me._sotService.GoToCheckout(scroll_position);
    });
  }

  public get allow_pay_at_terminal(): boolean {
    return this._sotService.payAtTerminal;
  }

  public get allow_pay_at_pos(): boolean {
    return this._sotService.payAtPOS;
  }

  public get allow_pay_at_card(): boolean {
    return this._sotService.payAtCard;
  }

  public get pay_at_terminal(): boolean {
    return this._sotService.GetPayType() == PaymentType.ecTerminal;
  }

  public get pay_at_pos(): boolean {
    return this._sotService.GetPayType() == PaymentType.atPOS;
  }

  public get allowVoucherPay(): boolean {
    return this._sotService.allowVoucherPay;
  }

  public get useBonusSystem(): boolean {
    return this._sotService.useBonussystem;
  }

  public selectPayAtTerminal(): void {
    const me = this;
    if (!me.is_basket_empty) {
      if (!me._canRedeemAllBenefits()) { return; }
      me._sotService.SetPayType(PaymentType.ecTerminal);
      if (me._sotService.GetSettings().request_tip) {
        me._sotService.OpenTippingDialog(() => {
          me._sotService.GoToPager();
        });
      } else {
        me._sotService.GoToPager();
      }
    }
  }

  public selectPayAtPOS(): void {
    const me = this;
    if (!me.is_basket_empty) {
      if (!me._canRedeemAllBenefits()) { return; }
      me._sotService.SetPayType(PaymentType.atPOS);
      if (me._sotService.GetSettings().request_tip) {
        me._sotService.OpenTippingDialog(() => {
          me._sotService.GoToPager();
        });
      } else {
        me._sotService.GoToPager();
      }
    }
  }

  public selectPayAtCard(): void {
    const me = this;
    if (!me.is_basket_empty) {
      if (!me._canRedeemAllBenefits()) { return; }
      if (me.isCardUnderLimit) {
        me._sotService.SetPayType(PaymentType.atCard);
        me._sotService.GoToPager();
      } else {
        this.showCardlimit();
      }
    }
  }

  //if use pager > pager scan
  // go to pager > pay type voucher > open voucher scan dialog
  public selectVoucherPay() {
    const me = this;
    if (!me.is_basket_empty) {
      if (!me._canRedeemAllBenefits()) { return; }
      me._sotService.voucherSelectedForPay = true;
      const use_tip = me._sotService.GetSettings().request_tip;
      if (use_tip) {
        me._sotService.OpenTippingDialog(() => {
          me._sotService.GoToPagerAndAddVoucher();
        });
      } else {
        me._sotService.GoToPagerAndAddVoucher();
      }
    }
  }

  public get vats(): SOTBasketVatViewModel[] {
    return this._sotService.GetBasket().getVats().vats
  }

  public get has_deposit(): boolean {
    return this.deposit > 0;
  }

  public get deposit(): number {
    return this._sotService.GetBasket().getDepositSum();
  }

  public get sum(): number {
    return this._sotService.GetBasket().getTotalSum();
  }

  public get is_basket_empty(): boolean {
    return this._sotService.GetBasket().getCount() === 0;
  }

  public pay(): void {
    const me = this;
    if (!me.is_basket_empty) {
      if (!me._canRedeemAllBenefits()) { return; }
      me._sotService.StartPayment();
    }
  }

  /** Check if the redeem benefit button can be displayed */
  public get canDisplayRedeemBenefitBtn() {
    return this._sotService.canDisplayRedeemBenefitBtn;
  }

  /** Is product a benefit item */
  public isBenefitItem(product: SOTBasketProductViewModel) {
    return product.isBenefitItem(product.benefitLossId, product.bonusactionId, product.benefitId);
  }

  /** Upper quantity bound for `sot-amount [max]` — locks coupon lines at qty=1, normal items at 99. */
  public maxAmountForProduct(product: SOTBasketProductViewModel): number {
    return product.isCouponItem ? 1 : MAX_BASKETITEM_COUNT;
  }

  /** Calculate the benefit points*/
  public benefitPoints(product: SOTBasketProductViewModel): number {
    return product.count * product.benefitCostPoints;
  }

  /** open bonus action dialog to edit benefit if the product is a benefit item */
  public openBonusActionDialogToEditBenefit(isBenefitItem: boolean) {
    const me = this;
    return me._sotService.openBonusActionDialogToEditBenefit(isBenefitItem);
  }

  /** open scan voucher dialog to redeem benefit */
  public async openScanVoucherToRedeemBenefit() {
    const me = this;
    me._sotService.AddVoucher(VoucherActionType.RedeemBenefit, true);
  }

  /** check if all basket benefits can be redeemed and open warning dialog if needed */
  private _canRedeemAllBenefits(): boolean {
    const me = this;
    if (me._sotService.hasUncoveredBenefits()) {
      me._sotService.openPointsCoverageWarningDialog();
      return false;
    }
    return true;
  }

  /* recommendations */

  private _show_recommendations: boolean = true;
  /** hält die recomendations damit sie nicht immer neu ermittelt werden müssen */
  private _recommendations: SOTProductViewModel[];
  /** seite, welche die recommendations zeigt */
  private _recommendation_page: number = 0;
  /** anzahl der empfählungen auf einer Seite */
  private static RECOMMENDATIONS_PER_SITE = 3;
  private static RECOMMENDATIONS_PER_SITE_MODE_ONE = 2;

  private get recommendationsPerSite(): number {
    return this.isModeOne ? CheckoutComponent.RECOMMENDATIONS_PER_SITE_MODE_ONE : CheckoutComponent.RECOMMENDATIONS_PER_SITE;
  }

  /** ermittelt bei bedarf die recomendations */
  private get recommendations(): SOTProductViewModel[] {
    const me = this;
    if (!me._recommendations) {
      me._recommendations = me._sotService.GetRecommendations();
    }
    return me._recommendations;
  }

  /** löscht die localen daten, damit sie neu geholt werden, PushChanges ist danach empfohlen */
  private resetRecommendations(): void {
    this._recommendations = null;
  }

  /** ist inital true (wenn es auch empfehlungen gibt), kann per closeRecommendations abgeschaltet werden */
  public get show_recommendations(): boolean {
    return this._show_recommendations && this.recommendations.length > 0;
  }

  /* ##### recomendation slider ##### */

  /** Interval in dem die Seiten der Recomendations automatisch durchlaufen werden */
  private static RECOMMENDATIONS_AUTO_SCROLL_INTERVAL = 5;

  private _recommendationsAutoScrollInterval: NodeJS.Timer;

  private resetRecomendationAutoScroll(): void {
    const me = this;
    me.stopRecomendationAutoScroll();
    me.startRecomendationAutoScroll();
  }

  private startRecomendationAutoScroll(): void {
    const me = this;
    if (me.show_recommendations && !me._recommendationsAutoScrollInterval) {
      me._recommendationsAutoScrollInterval = setInterval(() => { me.recomendationAutoScroll(); }
        , CheckoutComponent.RECOMMENDATIONS_AUTO_SCROLL_INTERVAL * 1000);
    }
  }

  private stopRecomendationAutoScroll(): void {
    const me = this;
    if (me._recommendationsAutoScrollInterval) {
      clearTimeout(me._recommendationsAutoScrollInterval);
      me._recommendationsAutoScrollInterval = null;
    }
  }

  private recomendationAutoScroll(): void {
    const me = this;
    if (me._recommendation_page < me.max_recomendation_page) {
      me.showNextRecommendations();
    } else {
      // reset to first page
      me._recommendation_page = 0;
      me.startRecomendationAutoScroll();
      me.PushChanges();
    }
  }

  /* ### END: recomendation slider ### */

  public closeRecommendations(): void {
    const me = this;
    me._show_recommendations = false;
    me.stopRecomendationAutoScroll();
    me.PushChanges();
  }

  public showPrevRecommendations(): void {
    const me = this;
    me._recommendation_page -= 1;
    me._recommendation_page = Math.max(me._recommendation_page, 0);
    me.resetRecomendationAutoScroll();
    me.PushChanges();
  }

  public get has_prev_recomendation_page(): boolean {
    return this._recommendation_page > 0;
  }

  public showNextRecommendations(): void {
    const me = this;
    me._recommendation_page += 1;
    me._recommendation_page = Math.min(me._recommendation_page, me.max_recomendation_page);
    me.resetRecomendationAutoScroll();
    me.PushChanges();
  }

  private get max_recomendation_page(): number {
    return Math.floor((this.recommendations.length - 1) / this.recommendationsPerSite); // -1 auf die length, weil position 0 basierend ist
  }

  public get has_next_recomendation_page(): boolean {
    return this._recommendation_page < this.max_recomendation_page;
  }

  public getRecommendationsForSite(): SOTProductViewModel[] {
    const me = this;
    const start_pos = me._recommendation_page * me.recommendationsPerSite;
    const end_pos = Math.min(start_pos + this.recommendationsPerSite, me.recommendations.length); // kein -1 da slice ausschliesend ist in JS
    return me.recommendations.slice(start_pos, end_pos);
  }

}
